/**
 * Auth Context
 *
 * Provides authentication state and actions.
 * Integrates Web3Auth for social login (Google, Facebook).
 * Manages session recovery and logout.
 * Handles first-time login profile completion.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import web3authGateway, { type Web3AuthUserInfo } from '../services/web3auth.gateway';
import { authApi } from '../services/auth.api';
import { useAuthStore, type AuthUser } from '../stores/useAuthStore';
import { useAuthNavigation } from '../hooks/useAuthNavigation';
import { FirstLoginDialog } from '@/shared/components/FirstLoginDialog/FirstLoginDialog';
import { COUNTRY_LIST } from '@/lib/country-list';
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from '@/lib/token-refresh';
import { isCredentialRejection, clearRejectedSession } from '../services/session-recovery';

interface AuthContextValue {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  user: AuthUser | null;
  error: string | null;

  // Actions
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The device-browser page an in-app webview opens for login. It completes the
 * login itself (register + bridge completion + closing the tab), so the app-wide
 * provider must not also react to a session restored there.
 */
const BRIDGE_LOGIN_PATH = '/auth/bridge-login';

/**
 * Carries a Facebook login across a page load in redirect mode (see `loginWithFacebook`).
 */
const LOGIN_HOP_PARAM = 'authStart';
const FACEBOOK_LOGIN_HOP_URL = `/login?${LOGIN_HOP_PARAM}=facebook`;

/** Removes the hop marker from the address bar, reporting whether it was there. */
function takeFacebookLoginHop(): boolean {
  const url = new URL(window.location.href);
  if (url.searchParams.get(LOGIN_HOP_PARAM) !== 'facebook') return false;

  // Stripped before the login starts, so going back to this page cannot restart it.
  url.searchParams.delete(LOGIN_HOP_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return true;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { navigateToHome, navigateToLogin, reloadAt } = useAuthNavigation();

  // Zustand store
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    setUser,
    setTokens,
    setWeb3AuthIdToken,
    setLoading,
    setError,
    logout: clearAuth
  } = useAuthStore();

  // Track if login is already in progress to prevent duplicate calls
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);

  // First-time login dialog state
  const [showFirstLoginDialog, setShowFirstLoginDialog] = useState(false);
  const [pendingAuthUser, setPendingAuthUser] = useState<AuthUser | null>(null);
  const [pseudoError, setPseudoError] = useState<string | null>(null);

  // Handle successful Web3Auth login
  const handleLoginSuccess = useCallback(
    async (userInfo: Web3AuthUserInfo) => {
      // Prevent duplicate calls. The login actions raise the busy flag before
      // they await the connector, so this early return is the one exit from
      // that flag which never reaches the `finally` below — lower it here or a
      // re-login while already authenticated strands the buttons disabled.
      if (isLoginInProgress || isAuthenticated) {
        setLoading(false);
        return;
      }

      setIsLoginInProgress(true);
      setLoading(true);

      try {
        if (!userInfo.idToken) {
          throw new Error('No idToken received from Web3Auth');
        }

        // Story 33-06: the gateway's mapper returns no typeOfLogin for a connection
        // it does not recognise, rather than substituting a literal. Registering a
        // placeholder would write it permanently into SOKA's Users.typeOfLogin, so
        // surface the mapper bug here instead — at login time, where it can be
        // investigated. This is not a credential rejection: the error carries no
        // status, so the catch below leaves the Web3Auth session intact.
        if (!userInfo.typeOfLogin) {
          throw new Error('No typeOfLogin received from Web3Auth');
        }

        // Save Web3Auth idToken
        setWeb3AuthIdToken(userInfo.idToken);

        // Register with SOKA backend
        const authResponse = await authApi.register(userInfo.idToken, {
          wallet: userInfo.wallet ?? '',
          typeOfLogin: userInfo.typeOfLogin,
          source: 'PWA'
        });

        // Save tokens
        setTokens(authResponse.accessToken, authResponse.refreshToken);

        // Start proactive token refresh scheduler
        startTokenRefreshScheduler();

        // Fetch user profile from SOKA API to get pseudo, points, email
        const profile = await authApi.getProfile();

        // Save user info (combine Web3Auth info + SOKA profile)
        const authUser: AuthUser = {
          // Web3Auth info
          wallet: userInfo.wallet,
          name: userInfo.name,
          profileImage: userInfo.profileImage,
          typeOfLogin: userInfo.typeOfLogin,
          verifierId: userInfo.verifierId,
          idToken: userInfo.idToken,
          // SOKA API profile info
          id: profile.id,
          email: profile.email || userInfo.email,
          pseudo: profile.pseudo,
          points: profile.points,
          country: profile.country
        };

        // Check if profile is incomplete (first-time login)
        const isProfileIncomplete = !profile.pseudo || !profile.country;

        if (isProfileIncomplete) {
          // Store pending user and show first-login dialog
          setPendingAuthUser(authUser);
          setShowFirstLoginDialog(true);
        } else {
          // Profile complete, save user and navigate to home
          setUser(authUser);
          navigateToHome();
        }
      } catch (err) {
        // SOKA rejecting the credential itself (401/403) leaves a cached Web3Auth
        // session that keeps restoring the same dead token forever. Clear it before
        // the error is surfaced — a transient failure (5xx, network) must stay
        // retryable and is left untouched.
        if (isCredentialRejection(err)) {
          await clearRejectedSession();
        }

        const errorMessage = err instanceof Error ? err.message : 'Login failed';
        setError(errorMessage);
        console.error('Login error:', err);
      } finally {
        setLoading(false);
        setIsLoginInProgress(false);
      }
    },
    [
      navigateToHome,
      setLoading,
      setWeb3AuthIdToken,
      setTokens,
      setUser,
      setError,
      isLoginInProgress,
      isAuthenticated
    ]
  );

  // Validate existing session on startup
  const validateSession = useCallback(async () => {
    const { accessToken, isAuthenticated: storedAuth } = useAuthStore.getState();

    // No stored session, nothing to validate
    if (!storedAuth || !accessToken) {
      return;
    }

    try {
      // Try to fetch profile to validate tokens are still valid
      const profile = await authApi.getProfile();

      // Update user data with fresh profile (in case it changed)
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser({
          ...currentUser,
          pseudo: profile.pseudo,
          points: profile.points,
          country: profile.country
        });
      }

      // Start proactive token refresh scheduler
      startTokenRefreshScheduler();
    } catch {
      // Token validation failed - clear stale session
      console.warn('Session validation failed, clearing stale auth state');
      clearAuth();
    }
  }, [setUser, clearAuth]);

  // Facebook login — same busy-flag rule as `loginWithGoogle` below.
  const connectFacebook = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      // connectWithFacebook triggers CONNECTED event which calls handleLoginSuccess
      // No need to call handleLoginSuccess explicitly (avoids duplicate registration)
      await web3authGateway.connectWithFacebook();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Facebook login failed';
      setError(errorMessage);
      setLoading(false);
      console.error('Facebook login error:', err);
    }
  }, [setLoading, setError]);

  // Initialize Web3Auth on mount and register CONNECTED event callback
  useEffect(() => {
    const initWeb3Auth = async () => {
      // The bridge tab is always a full page load (opened by the webview), so the
      // route read here is fixed for the life of this provider.
      const ownsLogin = window.location.pathname !== BRIDGE_LOGIN_PATH;

      // Returning from a redirect-mode login (Safari), the SDK takes a few seconds
      // to restore the session before registration can even start — keep the
      // login buttons in their loading state instead of looking idle.
      const isRestoringRedirect = ownsLogin && web3authGateway.isRestoringRedirect;
      if (isRestoringRedirect) setLoading(true);

      try {
        // Register callback for CONNECTED event (handles redirect flow + popup flow)
        if (ownsLogin) {
          web3authGateway.setOnLoginSuccessCallback((userInfo) => {
            void handleLoginSuccess(userInfo);
          });
        }

        const restored = await web3authGateway.init();
        // A restored session hands loading over to handleLoginSuccess.
        if (isRestoringRedirect && !restored) setLoading(false);

        if (ownsLogin && !restored && web3authGateway.usesRedirectLogin && takeFacebookLoginHop()) {
          void connectFacebook();
        }

        // Validate existing session if we have stored tokens
        // This catches stale sessions where tokens expired
        await validateSession();

        // Session recovery (redirect flow) is handled automatically by the CONNECTED event
        // in web3auth.gateway.ts which properly fetches the wallet address from the provider
      } catch (err) {
        if (isRestoringRedirect) setLoading(false);
        console.error('Web3Auth init error:', err);
      } finally {
        setIsInitialized(true);
      }
    };

    void initWeb3Auth();
  }, [handleLoginSuccess, validateSession, setLoading, connectFacebook]);

  // Google login
  //
  // The busy flag is raised HERE, not in the CONNECTED callback. The modal
  // closes the moment `connectTo` settles, but the gateway then reads the
  // session (wallet, claims, id token — with the citadel retry) before firing
  // the callback. Leaving the flag to the callback re-enables the login buttons
  // for that whole span and lets a second click open a second modal.
  const loginWithGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      // connectWithGoogle triggers CONNECTED event which calls handleLoginSuccess
      // No need to call handleLoginSuccess explicitly (avoids duplicate registration)
      await web3authGateway.connectWithGoogle();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google login failed';
      setError(errorMessage);
      setLoading(false);
      console.error('Google login error:', err);
    }
  }, [setLoading, setError]);

  // In redirect mode the Facebook redirect must not start from the tap: Android hands a
  // tap-triggered facebook.com navigation to the Facebook app and the tab never leaves.
  // Reload first; the fresh page starts the redirect on its own (see the init effect).
  const loginWithFacebook = useCallback(async () => {
    if (!web3authGateway.usesRedirectLogin) {
      await connectFacebook();
      return;
    }

    setError(null);
    setLoading(true);
    reloadAt(FACEBOOK_LOGIN_HOP_URL);
  }, [connectFacebook, reloadAt, setLoading, setError]);

  // Logout
  const logout = useCallback(async () => {
    setLoading(true);

    try {
      // Stop token refresh scheduler
      stopTokenRefreshScheduler();

      // Logout from SOKA backend (blacklist token)
      try {
        await authApi.logout();
      } catch {
        // Ignore backend logout errors
      }

      // Logout from Web3Auth
      await web3authGateway.logout();

      // Clear local auth state
      clearAuth();

      // Navigate to login
      navigateToLogin();
    } catch (err) {
      console.error('Logout error:', err);
      // Stop scheduler even on error
      stopTokenRefreshScheduler();
      // Force clear even on error
      clearAuth();
      navigateToLogin();
    } finally {
      setLoading(false);
    }
  }, [clearAuth, navigateToLogin, setLoading]);

  // Handle first-login dialog submission
  const handleFirstLoginSubmit = useCallback(
    async (data: { username: string; country: string }) => {
      if (!pendingAuthUser) return;

      setLoading(true);
      setError(null);

      try {
        // Update profile on SOKA API
        const updatedProfile = await authApi.updateProfile(data.username, data.country);

        // Update pending user with new profile data
        const completedUser: AuthUser = {
          ...pendingAuthUser,
          pseudo: updatedProfile.pseudo ?? data.username,
          country: updatedProfile.country ?? data.country
        };

        // Save user to store
        setUser(completedUser);

        // Close dialog and clean up
        setShowFirstLoginDialog(false);
        setPendingAuthUser(null);
        setPseudoError(null);

        // Navigate to home
        navigateToHome();
      } catch (err) {
        // Detect pseudo already taken (409 Conflict or 500 from API)
        const isAxiosError = err && typeof err === 'object' && 'response' in err;
        const status = isAxiosError
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;

        if (status === 409 || status === 500) {
          setPseudoError('Ce pseudo est déjà utilisé. Veuillez en choisir un autre.');
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
          setError(errorMessage);
        }
        console.error('First login profile update error:', err);
      } finally {
        setLoading(false);
      }
    },
    [pendingAuthUser, setLoading, setError, setUser, navigateToHome]
  );

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    isInitialized,
    user,
    error,
    loginWithGoogle,
    loginWithFacebook,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <FirstLoginDialog
        isOpen={showFirstLoginDialog}
        onSubmit={(data) => {
          setPseudoError(null);
          void handleFirstLoginSubmit(data);
        }}
        error={pseudoError}
        onErrorClear={() => {
          setPseudoError(null);
        }}
        countries={COUNTRY_LIST}
        defaultCountry="MG"
      />
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context
 *
 * @throws Error if used outside AuthProvider
 * @returns Authentication state and actions
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Get current user ID from auth store
 * Can be used outside React components
 *
 * @returns Current user numeric ID as string or undefined
 */
export function getCurrentUserId(): string | undefined {
  const { user, isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated || !user?.id) {
    return undefined;
  }
  return user.id.toString();
}
